import { Required } from 'utility-types'
import { jwtDecode } from 'jwt-decode'
import {
  batch,
  computed,
  effect,
  signal,
  untracked
} from '@preact/signals-core'

import { TypeID } from '@trustycars/genie-ui/types'

import { AuthAPI } from '$api/auth'
import { ObjectStorage } from '$store/localStorage'
import { currentTokenDetail, updateCurrent } from '.'

import { NAMESPACE } from './constants'

const storage = new ObjectStorage<Token[]>([NAMESPACE, 'tokens'])

const defaultTokensValue = () => storage.get([])

const applyDefaultStateValue = (
  states: Required<Partial<TokenState>, 'user_id'>
) => ({
  checked: false,
  loading: false,
  alive: false,
  ...states
})

export type DecodedToken = {
  /**
   * Could be used as user_id
   */
  sub: TypeID
  /**
   * Could be used as token_id
   */
  jti: TypeID
}

type NewState = Partial<Omit<TokenState, 'user_id'>>

/**
 * Type: JWT
 */
export type Token = string

export type TokenDetail = {
  token: Token

  /**
   * Decoded `jti` from `token`
   */
  id: DecodedToken['jti']

  /**
   * Decoded `sub` from `token`
   */
  user_id: DecodedToken['sub']
}

export type TokenState = Pick<TokenDetail, 'user_id'> & {
  /**
   * Indicate whether `token` validatity is checked
   */
  checked: boolean

  loading: boolean

  /**
   * `token` validatity
   */
  alive: boolean
}

export type TokenDetailWithState = TokenDetail & {
  state?: Omit<TokenState, 'user_id'>
}

/**
 * Type: Store
 */
export const tokens = signal(defaultTokensValue())

/**
 * Type: Store
 */
export const states = signal<TokenState[]>([])

/**
 * Type: Readonly
 */
export const details = computed<TokenDetail[]>(() =>
  tokens.value.map(token => {
    const { sub, jti } = jwtDecode<DecodedToken>(token)

    return {
      user_id: sub || token,
      id: jti,
      token
    }
  })
)

/**
 * Type: Readonly
 */
export const all_user_ids = computed(() =>
  details.value.map(({ user_id }) => user_id)
)

/**
 * Type: Readonly
 */
export const inactive_states = computed(() =>
  states.value.filter(({ alive, checked }) => checked && !alive)
)

/**
 * Type: Readonly
 */
export const with_state = computed<TokenDetailWithState[]>(() =>
  details.value.map(({ user_id, ...rest }) => ({
    user_id,
    state: states.value.find(({ user_id: suid }) => suid == user_id),
    ...rest
  }))
)

export const addToken = (token: Token) => {
  const user_id = jwtDecode<DecodedToken>(token).sub

  // Update the old token, if same `sub`
  const updatedTokens = [
    ...tokens.value.filter(eToken => {
      const old_id = jwtDecode<DecodedToken>(eToken).sub

      return user_id !== old_id
    }),
    token
  ]

  batch(() => {
    // Update the state too
    if (getState(user_id)) {
      addOrUpdateState(user_id, { checked: true, alive: true })
    }

    tokens.value = updatedTokens
  })

  return user_id
}

export const removeTokens = (user_ids: TokenState['user_id'][]) => {
  const allUserIDs = all_user_ids.value
  const activeSession = currentTokenDetail.value

  const indexes = user_ids
    .map(value => allUserIDs.findIndex(i => i === value))
    .filter(v => v !== -1)

  const newTokens = tokens.value.filter((_, index) => !indexes.includes(index))
  const newStates = states.value.filter(
    ({ user_id }) => !user_ids.includes(user_id)
  )

  batch(() => {
    tokens.value = newTokens
    states.value = newStates

    if (activeSession && allUserIDs.includes(activeSession?.user_id)) {
      updateCurrent(undefined)
    }
  })
}

const excludeStates = (user_id: TokenState['user_id']) => {
  return states.value.filter(state => state.user_id !== user_id)
}

const getState = (user_id: TokenState['user_id']) => {
  return states.value.find(state => state.user_id === user_id)
}

const removeState = (user_id: TokenState['user_id']) => {
  states.value = excludeStates(user_id)
}

const addOrUpdateState = (
  user_id: TokenState['user_id'],
  newState: NewState
) => {
  const statesValue = states.value

  const state = statesValue.find(s => s.user_id === user_id)

  // Update
  if (state) {
    states.value = statesValue.map(state => ({
      ...state,
      ...(state.user_id === user_id && newState)
    }))

    return
  }

  // Add
  states.value = [
    ...statesValue,
    applyDefaultStateValue({
      user_id,
      ...newState
    })
  ]
}

export const refreshStates = (force = false) => {
  details.value.map(async ({ user_id, token }) => {
    const api = new AuthAPI(token)

    const state = getState(user_id)

    // Skip when in-progress
    if (state?.loading) return

    // Check only once
    if (!force && state?.checked) return

    try {
      addOrUpdateState(user_id, { loading: true })

      await api.check()

      addOrUpdateState(user_id, { loading: false, checked: true, alive: true })
    } catch {
      addOrUpdateState(user_id, { loading: false, checked: true, alive: false })
    }
  })
}

/**
 * Type: Effect
 * Sync with storage
 */
tokens.subscribe(storage.set)
storage.watch(value => {
  tokens.value = value
})

/**
 * Type: Effect
 * Remove old states
 */
effect(() => {
  const user_id_values = all_user_ids.value
  const stateValues = states.value

  const outdatedIDs = stateValues
    .map(({ user_id }) => user_id)
    .filter(user_id => !user_id_values.includes(user_id))

  untracked(() => {
    batch(() => outdatedIDs.map(removeState))
  })
})

/**
 * Type: Effect
 * Refresh `states`
 */
details.subscribe(() => refreshStates())
